import { useCallback, useEffect, useState } from "react";
import { Coins, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ccApi, errMsg, fmtTs } from "./ccApi";
import { ActionBtn, Empty, Pill, Section } from "./ccUi";

const newKey = () => `promo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/* Part 7 — OWNER ONLY. Virtual promo coins; no cash, Stripe, payout or
 * earnings path is touched. Server enforces owner tier + idempotency. */
export default function PromoCoinsTab() {
  const [grants, setGrants] = useState([]);
  const [max, setMax] = useState(10000);
  const [form, setForm] = useState({ recipient: "", amount: "", reason: "", confirmation: "", idempotency_key: newKey() });
  const [busy, setBusy] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [roleForm, setRoleForm] = useState({ user_id: "", reason: "" });

  const refresh = useCallback(() => {
    ccApi.promoGrants().then((d) => { setGrants(d.grants); setMax(d.max_per_grant); }).catch(() => {});
    ccApi.safetyAdmins().then((d) => setAdmins(d.admins)).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const amount = parseInt(form.amount, 10) || 0;
  const ready = form.recipient.trim() && amount >= 1 && amount <= max && form.reason.trim().length >= 5 && form.confirmation.trim().toUpperCase() === `GRANT ${amount}`;
  const submit = async () => {
    setBusy(true);
    try {
      const d = await ccApi.promoGrant({ ...form, amount, recipient: form.recipient.trim() });
      toast.success(`Granted ${d.grant.amount} promo coins to @${d.grant.recipient_handle} · balance ${d.grant.balance_after}`);
      setForm({ recipient: "", amount: "", reason: "", confirmation: "", idempotency_key: newKey() });
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setRole = async (grant, user_id, reason) => {
    try { await ccApi.setSafetyAdmin({ user_id, grant, reason }); toast.success(grant ? "Safety Admin assigned" : "Safety Admin revoked"); setRoleForm({ user_id: "", reason: "" }); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-md p-3 flex gap-2" data-testid="cc-promo-banner">
        <Coins size={14} className="shrink-0 mt-0.5" />
        <span>Owner-only. Grants virtual promo coins with an immutable record + coin-ledger entry (<span className="font-mono">owner_promo_grant</span>). Never touches payouts, Stripe, creator earnings, the 45/55 split, purchases or cash balances. Max {max.toLocaleString()} per grant.</span>
      </div>
      <Section title="New promo grant" testid="cc-promo-form">
        <Input data-testid="cc-promo-recipient" value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} placeholder="Recipient @handle or user id" className="bg-black/40 border-white/10 text-white" />
        <Input data-testid="cc-promo-amount" type="number" min={1} max={max} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder={`Amount (1–${max})`} className="bg-black/40 border-white/10 text-white" />
        <Textarea data-testid="cc-promo-reason" rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason (required, recorded forever)" className="bg-black/40 border-white/10 text-white" />
        <Input data-testid="cc-promo-confirmation" value={form.confirmation} onChange={(e) => setForm({ ...form, confirmation: e.target.value })} placeholder={amount ? `Type GRANT ${amount} to confirm` : "Type GRANT <amount> to confirm"} className="bg-black/40 border-amber-500/40 text-white font-mono" />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-500 font-mono">idempotency {form.idempotency_key}</span>
          <ActionBtn tone="warn" testid="cc-promo-submit" disabled={!ready || busy} onClick={submit}>{busy ? "Granting…" : "Grant promo coins"}</ActionBtn>
        </div>
      </Section>
      <Section title={`Grant history · ${grants.length}`} testid="cc-promo-history">
        {grants.length === 0 && <Empty>No grants yet.</Empty>}
        {grants.map((g) => (
          <div key={g.id} data-testid={`cc-promo-row-${g.id}`} className="text-xs text-zinc-300 flex flex-wrap items-center gap-2 border-b border-white/5 last:border-0 py-1.5">
            <Pill tone={g.status === "completed" ? "green" : g.status === "failed" ? "red" : "amber"}>{g.status}</Pill>
            <span className="text-white">+{g.amount}</span> → @{g.recipient_handle}
            <span className="text-zinc-500">{fmtTs(g.created_at)}</span>
            <span className="italic text-zinc-400 truncate max-w-[220px]">“{g.reason}”</span>
          </div>
        ))}
      </Section>
      <Section title="Safety Admin roles (owner only)" testid="cc-safety-roles">
        {admins.map((a) => (
          <div key={a.id} className="flex items-center justify-between text-xs" data-testid={`cc-admin-row-${a.handle}`}>
            <span className="flex items-center gap-2 text-zinc-200"><ShieldCheck size={12} className={a.tier === "safety_admin" ? "text-sky-300" : "text-zinc-500"} />{a.display_name} <Pill tone={a.tier === "owner" ? "amber" : a.tier === "safety_admin" ? "blue" : "zinc"}>{a.tier.replace("_", " ")}</Pill></span>
            {a.tier === "safety_admin" && <ActionBtn tone="danger" testid={`cc-revoke-safety-${a.handle}`} onClick={() => { const reason = window.prompt("Reason for revoking Safety Admin (required):"); if (reason && reason.trim().length >= 5) setRole(false, a.id, reason.trim()); }}>Revoke</ActionBtn>}
          </div>
        ))}
        <div className="flex flex-col gap-2 pt-2 border-t border-white/10">
          <Input data-testid="cc-safety-user" value={roleForm.user_id} onChange={(e) => setRoleForm({ ...roleForm, user_id: e.target.value })} placeholder="@handle or id to make Safety Admin" className="bg-black/40 border-white/10 text-white text-xs" />
          <div className="flex gap-2">
            <Input data-testid="cc-safety-reason" value={roleForm.reason} onChange={(e) => setRoleForm({ ...roleForm, reason: e.target.value })} placeholder="Reason" className="bg-black/40 border-white/10 text-white text-xs" />
            <ActionBtn testid="cc-safety-assign" disabled={!roleForm.user_id.trim() || roleForm.reason.trim().length < 5} onClick={() => setRole(true, roleForm.user_id.trim(), roleForm.reason.trim())}>Assign</ActionBtn>
          </div>
        </div>
      </Section>
    </div>
  );
}
